import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, ChevronRight, Heart, Users, Wind, Compass, MessageCircle, Brain, RefreshCw } from 'lucide-react';
import { SmallText, BodyText } from './brand/BrandTypography';
import { trackGlobalToolUsage } from '../pages/UnifiedHome';

/**
 * RecommendedForYou - Personalized tool recommendations based on assessment
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Icon mapping
const ICON_MAP = {
    Heart, Users, Wind, Compass, MessageCircle, Brain, RefreshCw, Sparkles
};

export const RecommendedForYou = ({ className = '' }) => {
    const navigate = useNavigate();
    const [recommendations, setRecommendations] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchRecommendations = async () => {
            try {
                const token = localStorage.getItem('co_regulate_token');
                if (!token) {
                    setLoading(false);
                    return;
                }

                const res = await fetch(`${API_URL}/api/recommendations/personalized`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (res.ok) {
                    const data = await res.json();
                    setRecommendations(data);
                }
            } catch (error) {
                console.error('Failed to fetch recommendations:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRecommendations();
    }, []);

    if (loading) {
        return (
            <div className={`${className}`}>
                <div className="flex items-center gap-2 mb-4">
                    <Sparkles className="w-4 h-4 text-[#FFBB98]" />
                    <SmallText className="text-[#7D8E95] font-medium">Recommended for you</SmallText>
                </div>
                <div className="space-y-2">
                    {[1, 2].map(i => (
                        <div key={i} className="animate-pulse glass-card rounded-xl p-4 flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#FFBB98]/50 rounded-full" />
                            <div className="flex-1 space-y-2">
                                <div className="h-4 bg-[#FFBB98]/50 rounded w-1/3" />
                                <div className="h-3 bg-[#FFBB98]/50 rounded w-2/3" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (!recommendations?.has_recommendations) {
        return null;
    }

    const variantGlassClasses = {
        sage: 'glass-card-sage',
        rose: 'glass-card-rose',
        lavender: 'glass-card-lavender',
        blue: 'glass-card-blue'
    };

    const variantIconColors = {
        sage: 'text-[#1D6B6B]',
        rose: 'text-[#FFBB98]',
        lavender: 'text-[#7D8E95]',
        blue: 'text-[#7D8E95]'
    };

    const handleToolClick = (path) => {
        trackGlobalToolUsage(path);
        navigate(path);
    };

    return (
        <div className={`${className}`}>
            {/* Section Header */}
            <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-[#FFBB98]" />
                <SmallText className="text-[#7D8E95] font-medium">Recommended for you</SmallText>
            </div>

            {/* Recommendation Cards */}
            <div className="space-y-2">
                {recommendations.tools.map((tool, index) => {
                    const ToolIcon = ICON_MAP[tool.icon] || Heart;
                    const glassClass = variantGlassClasses[tool.variant] || variantGlassClasses.sage;
                    const iconColor = variantIconColors[tool.variant] || variantIconColors.sage;

                    return (
                        <button
                            key={tool.path}
                            onClick={() => handleToolClick(tool.path)}
                            className={`w-full ${glassClass} rounded-xl p-4 flex items-center gap-3 text-left 
                                       card-hover brand-slide-up`}
                            style={{ animationDelay: `${index * 50}ms` }}
                            data-testid={`recommendation-${index}`}
                        >
                            <div className={`w-10 h-10 rounded-full bg-white/60 backdrop-blur-sm flex items-center justify-center ${iconColor}`}>
                                <ToolIcon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-[#1D6B6B] text-sm">{tool.title}</p>
                                <p className="text-xs text-[#7D8E95] truncate">{tool.reason}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-[#7D8E95] flex-shrink-0" />
                        </button>
                    );
                })}
            </div>

            {/* Subtle context */}
            {recommendations.based_on?.primary_need && (
                <SmallText className="text-[#7D8E95] text-center mt-3 text-xs">
                    Based on your assessment
                </SmallText>
            )}
        </div>
    );
};

export default RecommendedForYou;
